// Shared primitive components: Pill, Button, Card, Icon, Money, etc.
import React from 'react';
// We only destructure hooks on lines that actually use them; this file doesn't
// use any hooks directly, but keeping React imported lets JSX compile.

// ---------- Icons (inline SVG, lucide-style) ----------
export function Icon({ name, size = 16, className = '', stroke = 'currentColor' }) {
  const s = size;
  const common = { width: s, height: s, viewBox: '0 0 24 24', fill: 'none', stroke, strokeWidth: 1.75, strokeLinecap: 'round', strokeLinejoin: 'round', className };
  const paths = {
    dashboard:   <><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></>,
    runs:        <><path d="M3 6h18M3 12h18M3 18h12"/></>,
    customers:   <><circle cx="12" cy="8" r="3.5"/><path d="M5 20c0-3.5 3-6.5 7-6.5s7 3 7 6.5"/></>,
    reps:        <><circle cx="9" cy="8" r="3"/><path d="M3 20c0-3 2.5-5.5 6-5.5s6 2.5 6 5.5"/><circle cx="17" cy="9" r="2.5"/><path d="M21 19c0-2.2-1.7-4-4-4"/></>,
    rates:       <><path d="M12 3v18M8 7h6.5a2.5 2.5 0 010 5H9a2.5 2.5 0 000 5h7"/></>,
    settings:    <><circle cx="12" cy="12" r="3"/><path d="M19 12a7 7 0 01-.1 1.2l2 1.6-2 3.4-2.4-1a7 7 0 01-2 1.2L14 21h-4l-.5-2.6a7 7 0 01-2-1.2l-2.4 1-2-3.4 2-1.6A7 7 0 015 12c0-.4 0-.8.1-1.2l-2-1.6 2-3.4 2.4 1a7 7 0 012-1.2L10 3h4l.5 2.6a7 7 0 012 1.2l2.4-1 2 3.4-2 1.6c.1.4.1.8.1 1.2z"/></>,
    audit:       <><path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z"/><path d="M14 3v6h6M9 14h6M9 18h4"/></>,
    search:      <><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></>,
    upload:      <><path d="M12 15V3M7 8l5-5 5 5"/><path d="M3 15v4a2 2 0 002 2h14a2 2 0 002-2v-4"/></>,
    download:    <><path d="M12 3v12M7 10l5 5 5-5"/><path d="M3 15v4a2 2 0 002 2h14a2 2 0 002-2v-4"/></>,
    check:       <><path d="M4 12l5 5L20 6"/></>,
    x:           <><path d="M6 6l12 12M18 6L6 18"/></>,
    warn:        <><path d="M12 3L2 21h20L12 3z"/><path d="M12 10v5M12 18v.01"/></>,
    info:        <><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8v.01"/></>,
    chevron:     <><path d="M9 6l6 6-6 6"/></>,
    chevronDown: <><path d="M6 9l6 6 6-6"/></>,
    kebab:       <><circle cx="12" cy="5" r="1.3"/><circle cx="12" cy="12" r="1.3"/><circle cx="12" cy="19" r="1.3"/></>,
    plus:        <><path d="M12 5v14M5 12h14"/></>,
    filter:      <><path d="M4 5h16L14 13v6l-4 2v-8L4 5z"/></>,
    close:       <><path d="M6 6l12 12M18 6L6 18"/></>,
    file:        <><path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z"/><path d="M14 3v6h6"/></>,
    arrow:       <><path d="M5 12h14M13 6l6 6-6 6"/></>,
    lock:        <><rect x="5" y="11" width="14" height="9" rx="1.5"/><path d="M8 11V8a4 4 0 118 0v3"/></>,
    unlock:      <><rect x="5" y="11" width="14" height="9" rx="1.5"/><path d="M8 11V8a4 4 0 017.5-2"/></>,
    calendar:    <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></>,
    sparkle:     <><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M6 18l2.5-2.5M15.5 8.5L18 6"/></>,
    trend:       <><path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/></>,
    override:    <><path d="M4 4h16v16H4z" strokeDasharray="3 3"/><path d="M8 12h8M12 8v8"/></>,
    userMenu:    <><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7"/></>,
    history:     <><path d="M3 12a9 9 0 109-9 9 9 0 00-7.5 4"/><path d="M3 3v5h5M12 7v5l3 2"/></>,
    unpaid:      <><path d="M12 3v18M17 6H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></>,
    thisMonth:   <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18"/><rect x="7" y="13" width="4" height="4"/></>,
    tweak:       <><path d="M4 6h10M4 12h16M4 18h6"/><circle cx="16" cy="6" r="2"/><circle cx="12" cy="18" r="2"/></>,
  };
  return <svg {...common}>{paths[name] || null}</svg>;
}

// ---------- Primitives ----------
export function Pill({ tone = 'neutral', children, className = '' }) {
  const tones = {
    neutral: 'bg-neutral-100 text-neutral-700 border-neutral-200',
    draft:   'bg-amber-50  text-amber-800  border-amber-200',
    finalized:'bg-emerald-50 text-emerald-800 border-emerald-200',
    paid:    'bg-emerald-50 text-emerald-800 border-emerald-200',
    unpaid:  'bg-amber-50  text-amber-800  border-amber-200',
    warn:    'bg-amber-50  text-amber-800  border-amber-200',
    error:   'bg-red-50    text-red-800    border-red-200',
    info:    'bg-blue-50   text-blue-800   border-blue-200',
    y1:      'bg-violet-50 text-violet-800 border-violet-200',
    y2:      'bg-sky-50    text-sky-800    border-sky-200',
    brand:   'bg-primary-100 text-primary-900 border-primary-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-[2px] text-[11px] font-semibold uppercase tracking-[0.04em] rounded-full border ${tones[tone] || tones.neutral} ${className}`}>
      {children}
    </span>
  );
}

export function Button({ variant = 'secondary', size = 'md', icon, children, className = '', onClick, disabled, type = 'button' }) {
  const sizes = {
    sm: 'h-7  px-2.5 text-[12px] rounded-md gap-1',
    md: 'h-9  px-3.5 text-[13px] rounded-md gap-1.5',
    lg: 'h-10 px-4   text-[14px] rounded-md gap-1.5',
  };
  const variants = {
    primary:    'bg-primary-700 text-white hover:bg-primary-900 border border-primary-700 shadow-sm',
    success:    'bg-emerald-700 text-white hover:bg-emerald-800 border border-emerald-700 shadow-sm',
    destructive:'bg-red-700 text-white hover:bg-red-800 border border-red-700 shadow-sm',
    secondary:  'bg-white text-neutral-900 hover:bg-neutral-50 border border-neutral-300',
    ghost:      'bg-transparent text-neutral-700 hover:bg-neutral-100 border border-transparent',
    tertiary:   'bg-transparent text-primary-700 hover:text-primary-900 hover:bg-primary-100 border border-transparent',
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      className={`inline-flex items-center justify-center font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${sizes[size]} ${variants[variant]} ${className}`}>
      {icon && <Icon name={icon} size={size==='sm'?13:15}/>}
      {children}
    </button>
  );
}

export function Card({ children, className = '', padding = 'p-5', onClick, hover = false }) {
  return (
    <div onClick={onClick}
      className={`bg-white border border-neutral-200 rounded-[8px] ${padding} ${hover ? 'hover:border-neutral-300 hover:shadow-sm transition' : ''} ${onClick ? 'cursor-pointer' : ''} ${className}`}>
      {children}
    </div>
  );
}

export function Money({ value, className = '', sign = true, dash = true }) {
  if ((value === 0 || value === null || value === undefined) && dash) {
    return <span className={`font-mono text-neutral-400 ${className}`}>—</span>;
  }
  const n = Number(value);
  const formatted = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return <span className={`font-mono tabular-nums ${className}`}>{sign ? '$' : ''}{n < 0 ? `(${formatted})` : formatted}</span>;
}

export function Num({ value, className = '' }) {
  return <span className={`font-mono tabular-nums ${className}`}>{Number(value).toLocaleString('en-US')}</span>;
}

export function Banner({ tone = 'info', icon = 'info', title, children, onDismiss, action }) {
  const tones = {
    info:    'bg-blue-50   border-blue-200   text-blue-900',
    warn:    'bg-amber-50  border-amber-200  text-amber-900',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    error:   'bg-red-50    border-red-200    text-red-900',
  };
  return (
    <div className={`flex items-start gap-3 border rounded-md px-4 py-3 text-[13px] ${tones[tone] || tones.info}`}>
      <div className="mt-[1px]"><Icon name={icon} size={16}/></div>
      <div className="flex-1">
        {title && <div className="font-semibold">{title}</div>}
        <div className={title ? 'mt-0.5 opacity-90' : ''}>{children}</div>
      </div>
      {action}
      {onDismiss && <button className="opacity-60 hover:opacity-100" onClick={onDismiss}><Icon name="close" size={14}/></button>}
    </div>
  );
}

export function KPICard({ label, value, sub, tone = 'neutral', cta, onClick }) {
  return (
    <Card hover={!!onClick} onClick={onClick} padding="p-5" className="flex flex-col gap-2">
      <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">{label}</div>
      <div className="text-[26px] font-bold tracking-[-0.01em] leading-none text-neutral-900 font-mono tabular-nums">{value}</div>
      {sub && <div className="text-[12px] text-neutral-500">{sub}</div>}
      {cta && <div className="mt-2"><Button size="sm" variant={tone === 'warn' ? 'secondary' : 'tertiary'} icon="arrow">{cta}</Button></div>}
    </Card>
  );
}

export function Section({ title, right, children, sub }) {
  return (
    <section className="space-y-3">
      {(title || right) && (
        <div className="flex items-end justify-between gap-4">
          <div>
            {title && <h2 className="text-[18px] font-semibold text-neutral-900 tracking-[-0.01em]">{title}</h2>}
            {sub && <div className="text-[12px] text-neutral-500 mt-0.5">{sub}</div>}
          </div>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export function Select({ value, onChange, options, placeholder = 'Select…', size = 'md' }) {
  const sizes = { sm: 'h-7 text-[12px] px-2', md: 'h-9 text-[13px] px-2.5' };
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      className={`${sizes[size]} rounded-md border border-neutral-300 bg-white hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700`}>
      <option value="" disabled>{placeholder}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function Input({ value, onChange, placeholder, icon, size = 'md', className = '' }) {
  const sizes = { sm: 'h-7 text-[12px]', md: 'h-9 text-[13px]' };
  return (
    <div className={`relative flex items-center ${className}`}>
      {icon && <Icon name={icon} size={14} className="absolute left-2.5 text-neutral-400"/>}
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className={`${sizes[size]} w-full rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 ${icon ? 'pl-8 pr-2.5' : 'px-2.5'}`}/>
    </div>
  );
}

// No window-globals publishing. Every component is exported above;
// other files get them via `import { Card, Button, ... } from './ui.jsx'`.
